import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Input, Tabs, TabsList, Tab, TabPanel, Textarea } from "@wmart/ui";
import { LIVE_LOCALE_CODES, localeName, type Locale } from "@wmart/i18n";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";

type Kind = "product" | "category" | "brand";

const FIELDS: Record<Kind, Array<{ key: string; label: string; multiline?: boolean }>> = {
  product: [
    { key: "title", label: "Title" },
    { key: "description", label: "Description", multiline: true },
    { key: "ingredients", label: "Ingredients", multiline: true },
    { key: "storage_notes", label: "Storage notes", multiline: true },
  ],
  category: [{ key: "title", label: "Title" }],
  brand: [{ key: "title", label: "Title" }],
};

interface Row {
  locale: Locale;
  fields: Record<string, string | null>;
  source: "machine" | "human";
  stale: boolean;
}
interface Payload {
  base: Record<string, unknown>;
  translations: Row[];
}

const TARGET_LOCALES = LIVE_LOCALE_CODES.filter((c) => c !== "en") as Locale[];

export function TranslationEditor({ kind, id }: { kind: Kind; id: string }) {
  const { notify, notifyProblem } = useToast();
  const [payload, setPayload] = useState<Payload | null>(null);
  const [drafts, setDrafts] = useState<Record<Locale, Record<string, string>>>(
    {} as Record<Locale, Record<string, string>>,
  );
  const [busy, setBusy] = useState<Locale | null>(null);

  const path = `/v1/admin/${kind === "category" ? "categories" : `${kind}s`}/${id}/translations`;

  async function load() {
    try {
      const data = await api.get<Payload>(path);
      setPayload(data);
      const next = {} as Record<Locale, Record<string, string>>;
      for (const loc of TARGET_LOCALES) {
        const row = data.translations.find((r) => r.locale === loc);
        next[loc] = {};
        for (const f of FIELDS[kind]) next[loc][f.key] = (row?.fields[f.key] as string) ?? "";
      }
      setDrafts(next);
    } catch (e) {
      if (e instanceof ApiError && e.problem) notifyProblem(e.problem);
      else notify("error", "Failed to load translations");
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id]);

  const rowByLocale = useMemo(() => {
    const m = new Map<Locale, Row>();
    for (const r of payload?.translations ?? []) m.set(r.locale, r);
    return m;
  }, [payload]);

  async function save(locale: Locale) {
    setBusy(locale);
    try {
      const body: Record<string, string | null> = {};
      for (const f of FIELDS[kind]) {
        const v = drafts[locale]?.[f.key] ?? "";
        body[f.key] = v.trim() === "" ? null : v;
      }
      await api.put(`${path}/${locale}`, body);
      notify("success", `Saved ${localeName(locale)} translation`);
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.problem) notifyProblem(e.problem);
      else notify("error", "Failed to save translation");
    } finally {
      setBusy(null);
    }
  }

  async function retranslate(locale: Locale) {
    setBusy(locale);
    try {
      await api.post(`${path}/${locale}/retranslate`, {});
      notify("success", `Re-translation queued for ${localeName(locale)} — refresh shortly`);
    } catch (e) {
      if (e instanceof ApiError && e.problem) notifyProblem(e.problem);
      else notify("error", "Failed to queue re-translation");
    } finally {
      setBusy(null);
    }
  }

  if (!payload) return <p className="text-ink-500 text-body">Loading translations…</p>;

  if (TARGET_LOCALES.length === 0) return null;

  return (
    <Tabs defaultValue={TARGET_LOCALES[0]!}>
      <TabsList>
        {TARGET_LOCALES.map((loc) => (
          <Tab key={loc} value={loc}>
            {localeName(loc)}
          </Tab>
        ))}
      </TabsList>
      {TARGET_LOCALES.map((loc) => {
        const row = rowByLocale.get(loc);
        return (
          <TabPanel key={loc} value={loc}>
            <div className="flex items-center gap-2 mb-3">
              <Badge className={row?.source === "human" ? "bg-emerald-100 text-emerald-800" : ""}>
                {row ? row.source : "untranslated"}
              </Badge>
              {row?.stale && <Badge className="bg-amber-100 text-amber-800">needs review</Badge>}
            </div>
            <div className="space-y-4">
              {FIELDS[kind].map((f) => {
                const baseVal = (payload.base[f.key] as string) ?? "";
                const draftVal = drafts[loc]?.[f.key] ?? "";
                const common = {
                  label: `${f.label} (${localeName(loc)})`,
                  value: draftVal,
                  dir: "rtl" as const,
                };
                return (
                  <div key={f.key} className="grid grid-cols-2 gap-3">
                    {f.multiline ? (
                      <Textarea
                        label={common.label}
                        value={common.value}
                        dir={common.dir}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          setDrafts((d) => ({ ...d, [loc]: { ...d[loc], [f.key]: e.target.value } }))
                        }
                      />
                    ) : (
                      <Input
                        label={common.label}
                        value={common.value}
                        dir={common.dir}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setDrafts((d) => ({ ...d, [loc]: { ...d[loc], [f.key]: e.target.value } }))
                        }
                      />
                    )}
                    <div>
                      <p className="text-caption text-ink-500 mb-1">{f.label} — English (reference)</p>
                      <p className="text-body text-ink-700 whitespace-pre-wrap">{baseVal || "—"}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => void save(loc)} disabled={busy === loc}>Save</Button>
              <Button variant="secondary" onClick={() => void retranslate(loc)} disabled={busy === loc}>
                Re-translate
              </Button>
            </div>
          </TabPanel>
        );
      })}
    </Tabs>
  );
}
